import {createContext, useCallback, useContext, useEffect, useReducer, useRef, useState} from "react";
import {parseMatchTiming, PermissiveURLSearchParams, usePersistentWebsocketWithTime} from "../utils";

import Config, {DisplayConfig} from './Config';
import Blank from "./pages/Blank";
import Debug from "./pages/Debug";

import './displays.css';
import Video from "./pages/Video";
import Preview from "./pages/Preview";
import Rankings from "./pages/Rankings";
import Match from "./pages/Match";
import Sponsors from "./pages/Sponsors";
import DisconnectedBanner from "./components/DisconnectedBanner";
import Message from "./pages/Message";
import WifiReminder from "./pages/WifiReminder";
import AllianceSelection from "./pages/AllianceSelection";
import Bracket, {RotatingBracket} from "./pages/Bracket";
import EnsureAudio from "./EnsureAudio";
import {TimerData, useTimer} from "../hooks";
import Award from "./pages/Award";
import Advancement from "./pages/Advancement";
import Randomization from "./pages/Randomization";
import Results from "./pages/Results";
import AudienceKey from "./pages/AudienceKey";
import OnlineResults from "./pages/OnlineResults";
import {
    AllianceLastMatchTimeout,
    DisplayCommand,
    DisplayCommandType,
    LastOnFieldTimeout,
    MatchInfo,
    MatchTeam, MatchTiming,
    PitMessage,
    ScheduledStartTimeout,
    ScoreUpdate,
    SetConfig,
    T206Timeout,
    TimeoutReference,
    TimeoutType,
    TimeoutUpdate
} from "../javaTypes";
import DisplayParams, {DisplayParamsType} from "./DisplayParams";
import {
    isCancelMatch,
    isInMatchUpdate,
    isMatchUpdate,
    isPitUpdate, isPlayVideo,
    isRandomization,
    isRedirect,
    isScoreUpdate,
    isShowAdvancement,
    isShowAward,
    isShowBlank,
    isShowBracket,
    isShowInspectionStatus,
    isShowKey,
    isShowMessage,
    isShowOnlineResults,
    isShowPreview,
    isShowRankings,
    isShowResults, isShowRotatingBracket,
    isShowRoundResults,
    isShowSafetySecurity,
    isShowSelection,
    isShowSetup,
    isShowSlideshow,
    isShowSponsors,
    isShowVideo,
    isShowWifi,
    isTimeoutUpdate
} from "./commandFilters";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import InspectionStatus from "./pages/InspectionStatus";
import SafetySecurity from "./pages/SafetySecurity";
import Slideshow from "./pages/Slideshow";
import {RankingData, useRankingState} from "./rankingDataState";
import DisplayErrorBoundary from "./DisplayErrorBoundary";
import {isT206DependencyTimeout} from "../timeoutUtils";
import {getExpectedStartTimer} from "./util";
import PlayVideo from "./pages/PlayVideo";
import { Temporal } from "temporal-polyfill";
import RoundResults from "./pages/RoundResults";

export interface ActiveTimeout {
    timer: TimerData,
    nextMatch?: MatchInfo<MatchTeam>,
    nextMatchIndex?: number,
    timeout?: TimeoutReference
}
export const Timeout = createContext<ActiveTimeout>({ timer: {running: false, remaining: 0} });

const LIVE_UPDATES = [DisplayCommandType.SHOW_RANDOM, DisplayCommandType.RANDOMIZE, DisplayCommandType.SHOW_MATCH, DisplayCommandType.START_MATCH, DisplayCommandType.ABORT_MATCH];
const AUDIENCE_UPDATES = [
    DisplayCommandType.SHOW_PREVIEW,
    DisplayCommandType.SHOW_RANDOM,
    DisplayCommandType.SHOW_MATCH,
    DisplayCommandType.START_MATCH,
    DisplayCommandType.ABORT_MATCH,
    DisplayCommandType.SHOW_RESULTS,
    DisplayCommandType.SHOW_ROUND_RESULTS,
    DisplayCommandType.SHOW_SPONSORS,
    DisplayCommandType.SHOW_TIMEOUT,
    DisplayCommandType.SHOW_SELECTION,
    DisplayCommandType.SHOW_STATUS,
    DisplayCommandType.SHOW_AWARD,
    DisplayCommandType.SHOW_BRACKET,
    DisplayCommandType.REDIRECT,
    DisplayCommandType.SHOW_MESSAGE,
    DisplayCommandType.SHOW_WIFI,
    DisplayCommandType.SHOW_SAFETY_SECURITY,
    DisplayCommandType.SHOW_SLIDESHOW,
    DisplayCommandType.SHOW_BLANK,
    DisplayCommandType.SHOW_VIDEO,
    DisplayCommandType.PLAY_VIDEO,
    DisplayCommandType.SHOW_KEY,
    DisplayCommandType.SHOW_ONLINE_RESULTS,
    DisplayCommandType.SHOW_RANKINGS,
    DisplayCommandType.SHOW_INSPECTION_STATUS,
    DisplayCommandType.SHOW_ADVANCEMENT
];

const AUDIENCE_BAR_UPDATES = [
    DisplayCommandType.SHOW_PREVIEW,
    DisplayCommandType.SHOW_MATCH,
    DisplayCommandType.START_MATCH,
    DisplayCommandType.ABORT_MATCH,
    DisplayCommandType.SHOW_RESULTS,
    DisplayCommandType.SHOW_ROUND_RESULTS,
    DisplayCommandType.SHOW_BLANK,
    DisplayCommandType.SHOW_VIDEO,
    DisplayCommandType.REDIRECT,
    DisplayCommandType.SHOW_AWARD,
    DisplayCommandType.SHOW_SELECTION,
]

const FIELD_UPDATES = [
    DisplayCommandType.SHOW_SETUP,
    DisplayCommandType.SHOW_PREVIEW, // allows the FDs to go backwards if the AD goes backwards
    DisplayCommandType.RANDOMIZE,
    DisplayCommandType.SHOW_RANDOM,
    DisplayCommandType.SHOW_MATCH,
    DisplayCommandType.START_MATCH,
    DisplayCommandType.ABORT_MATCH,
    DisplayCommandType.SHOW_RESULTS,
    DisplayCommandType.SHOW_ROUND_RESULTS,
    DisplayCommandType.REDIRECT,
    DisplayCommandType.SHOW_TIMEOUT,
    DisplayCommandType.END_TIMEOUT,
    // DisplayCommandType.TIMEOUT_UPDATE,
    DisplayCommandType.SHOW_SELECTION,
    DisplayCommandType.SHOW_BLANK,
    DisplayCommandType.SHOW_KEY
];

const PIT_UPDATES = [
    DisplayCommandType.REDIRECT
]

type SavedUpdates = Record<`${DisplayCommandType}-${number}`, DisplayCommand<unknown>>

type PageParams<T> = {
    latestTimeout?: DisplayCommand<TimeoutUpdate>,
    command: DisplayCommand<T>,
    scoreUpdate?: DisplayCommand<ScoreUpdate>,
    showLeagueRankings?: boolean,
    nowCallback: (() => number) | (() => any),
    config: DisplayConfig
}

function narrowPageParams<T>(filter: (msg: DisplayCommand<any>) => msg is DisplayCommand<T>, msg: PageParams<any>): msg is PageParams<T> {
    return filter(msg.command)
}

function updateReducer(state : SavedUpdates, update: DisplayCommand<unknown>) {
    if(isCancelMatch(update)) {
        // All we are doing is filtering, so even though JS collapses the type to a string, it's safe to cast it back
        return Object.fromEntries(Object.entries(state).filter(([_key, command]) => {
            if(!isMatchUpdate(command)) {
                return true
            }

            return command.params.number == update.params.number && command.index < update.index
        })) as SavedUpdates
    }
    // We don't need to capture redirects, and they can have surprising results
    if(isRedirect(update)) {
        return state
    }

    return Object.assign({}, state, { [`${update.type}-${update.field}`] : update });
}

function eventFilter(whitelist : DisplayCommandType[], field : string, fieldCount : number) {
    return (evt : DisplayCommand<unknown>) => {
        if (field !== 'all' && ((evt.field > 0 && evt.field != parseInt(field)) || (isMatchUpdate(evt) && evt.params.field > 0 && evt.params.field !== parseInt(field)))) {
            return false;
        }
        return whitelist.includes(evt.type);
    }
}

const blankUpdate = { type: DisplayCommandType.SHOW_BLANK, params: {}, ts: 0, index: -1, field: 0, init: false };
function getLastRelevantUpdate(updates : SavedUpdates, config : DisplayConfig, matchDuration: Temporal.Duration | undefined, fieldCount : number, qualsStarted : boolean, inElims : boolean, inDivisionalElims : boolean) : DisplayCommand<unknown> {
    const allUpdates = Object.values(updates);

    allUpdates.sort((a, b) => b.ts - a.ts);
    let filter = eventFilter(AUDIENCE_UPDATES, config.bindToField, fieldCount);
    let defaultType = DisplayCommandType.SHOW_BLANK;
    if (config.type === 'field') {
        filter = eventFilter(FIELD_UPDATES, config.bindToField, fieldCount);
    }
    if (config.type === 'cmpBar') {
        filter = eventFilter(AUDIENCE_BAR_UPDATES, config.bindToField, fieldCount);
    }
    if (config.type === 'sponsor') {
        filter = () => false;
        defaultType = DisplayCommandType.SHOW_SPONSORS;
    }
    if (config.type === 'pit') {
        filter = eventFilter(PIT_UPDATES, 'all', fieldCount);
        if(inElims) {
            defaultType = DisplayCommandType.SHOW_BRACKET;
        } else if(inDivisionalElims) {
            defaultType = DisplayCommandType.SHOW_ROTATING_BRACKET;
        // TODO plumb inspection status into cloud pit?? Cloud knows it but plumbing it is a pain
        } else if(qualsStarted || window.displayParams.style === "cloudPit") {
            defaultType = DisplayCommandType.SHOW_RANKINGS;
        } else {
            defaultType = DisplayCommandType.SHOW_INSPECTION_STATUS;
        }
    }
    const filteredUpdates = allUpdates.filter(filter);
    let bestUpdate = filteredUpdates.length > 0 ? filteredUpdates[0] : { type: defaultType, params: { }, ts: 0, index: -1, field: 0, init: false };

    const startMatch = filteredUpdates.find((up) => up.type === DisplayCommandType.START_MATCH)
    const abortMatch = filteredUpdates.find((up) => up.type === DisplayCommandType.ABORT_MATCH)
    // If we are in-match, the best update is always the corresponding START_MATCH
    if(startMatch !== undefined && matchDuration) {
        if(bestUpdate.ts - startMatch.ts < matchDuration.total('milliseconds') && (abortMatch === undefined || abortMatch.ts < startMatch.ts)) {
            bestUpdate = startMatch
        }
    }

    if(config.type == 'field' && [DisplayCommandType.SHOW_RESULTS, DisplayCommandType.SHOW_ROUND_RESULTS].includes(bestUpdate.type)) {
        // Look for setups sent *after* the last match started (and that would therefore have otherwise been ignored), otherwise show blank
        bestUpdate = filteredUpdates.find((up) => startMatch && up.type == DisplayCommandType.SHOW_SETUP && up.ts > startMatch.ts) || blankUpdate;
    }
    // cmpBars can't usefully present round results, but they shouldn't stay on the last match when we post them
    if(config.type == 'cmpBar' && bestUpdate.type == DisplayCommandType.SHOW_ROUND_RESULTS) {
        bestUpdate = blankUpdate;
    }
    // When matches are generated field displays should go blank
    if(config.type == 'field' && isShowSelection(bestUpdate) && bestUpdate.params.state?.immutable) {
        bestUpdate = blankUpdate;
    }
    return bestUpdate;
}

// nextMatch is the next match _this_ display will see
// Also need to know if nextMatch != nextMatches[0] - ie we are waiting on another field
function isApplicableTimeout(nextMatch: MatchInfo<MatchTeam>, timeout: TimeoutReference) {
    switch (timeout.type) {
        case TimeoutType.G301: return false;
        case TimeoutType.RUNNING_MATCH: return true; // or do we need this so we can show "Don't Care"
        case TimeoutType.SCHEDULED_START: return (timeout as ScheduledStartTimeout).match === nextMatch?.number;
        case TimeoutType.T206: return (timeout as T206Timeout).targetMatch === nextMatch.number;
        case TimeoutType.LAST_ON_FIELD: return (timeout as LastOnFieldTimeout).field === nextMatch.field;
        case TimeoutType.ALLIANCE_LAST: return [nextMatch.red.seed, nextMatch.blue.seed].includes((timeout as AllianceLastMatchTimeout).alliance);
        case TimeoutType.T206_MATCH_DEPENDENCY: return true;
    }
}

function getLastRelevantTimeout(updates : SavedUpdates, config : DisplayConfig, fieldCount : number) {
    const allUpdates = Object.values(updates);
    allUpdates.sort((a, b) => b.ts - a.ts);
    const filteredUpdates = allUpdates.filter(isTimeoutUpdate);
    if (filteredUpdates.length === 0) {
        return undefined;
    }
    //regardless of field, last update will have the full timeout info - if we are bound to a field, filter the params for TOs we care about downstream
    return filteredUpdates[0]
}

function getLastRelevantScoreUpdate(updates : SavedUpdates, lastUpdate : DisplayCommand<unknown>) {
    if (!lastUpdate || !LIVE_UPDATES.includes(lastUpdate.type))
    {
        return undefined;
    }

    const allUpdates = Object.values(updates);

    allUpdates.sort((a, b) => b.ts - a.ts);
    const filteredUpdates = allUpdates.filter(isScoreUpdate).filter((u) => isMatchUpdate(lastUpdate) && u.params.matchName === lastUpdate.params.matchName);
    return filteredUpdates.length > 0 ? filteredUpdates[0] : undefined
}


export const initialConfig : DisplayConfig = {
    type: 'audience',
    bindToField: 'all',
    scoringBarLocation: 'bottom',
    allianceOrientation: 'standard',
    mute: false,
    muteRandomizationResults: false,
    inspectionGroupDivision: false,
    fieldStyleTimer: true,
    overlay: false,
    overlayColor: window.obsstudio ? 'transparent' : '#00FF00',
    allianceSelectionStyle: 'classic',
    awardsStyle: 'overlay',
    dualDivisionRankingStyle: 'sideBySide',
    rankingsFontSize: 'larger',
    showMeetRankings: false,
    rankingsAllTeams: true
}

function transitionKey(type: DisplayCommandType) {
    if([DisplayCommandType.SHOW_MATCH, DisplayCommandType.START_MATCH, DisplayCommandType.ABORT_MATCH].includes(type)) {
        return DisplayCommandType.SHOW_MATCH
    }
    if([DisplayCommandType.SHOW_SETUP, DisplayCommandType.SHOW_PREVIEW].includes(type)) {
        return DisplayCommandType.SHOW_PREVIEW
    }
    if([DisplayCommandType.SHOW_RANDOM, DisplayCommandType.RANDOMIZE].includes(type)) {
        return DisplayCommandType.SHOW_RANDOM
    }
    return type;
}

function useCursorMovedRecently() {
    const [movedRecently, setMovedRecently] = useState(true);
    const mouseMoveTimeout = useRef<number | undefined>(undefined);

    useEffect(() => {
        mouseMoveTimeout.current = window.setTimeout(() => { setMovedRecently(false)}, 1000)
        const mouseMoveListener = () => {
            setMovedRecently(true)

            clearTimeout(mouseMoveTimeout.current);
            mouseMoveTimeout.current = window.setTimeout(() => { setMovedRecently(false)}, 1000)
        }

        document.addEventListener('mousemove', mouseMoveListener)

        return () => {
            if(mouseMoveListener) {
                document.removeEventListener('mousemove', mouseMoveListener)
            }
        }
    }, [])

    return movedRecently
}

export default function DisplayContainer({ reconfigure }:  {reconfigure: (newParams: DisplayParamsType) => void}) {
    const displayParams = useContext(DisplayParams)
    const {
        liveUpdateUrl,
        eventName,
        eventCode,
        fieldCount,
        isDualDivisionParent,
        divisionNumber,
        isLeagueMeet,
        showMeetRankings,
        style,
        missingLeague,
        isFirstChampionship
    } = displayParams;
    const [updates, dispatchUpdate] = useReducer(updateReducer, {} as SavedUpdates);
    const [rankingData, updateRankingData] = useRankingState();
    const [config, setConfig] = useState<DisplayConfig>(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const valueFromQuery = Object.assign({}, initialConfig, { type: style === 'cloudPit' ? 'pit' : 'audience'});
        searchParams.forEach((value: string | boolean | number[], key : string) => {
            if(value === 'true') {
                value = true;
            }
            if(value === 'false') {
                value = false;
            }
            if(key == 'divisionOrder' && typeof value == 'string') {
                value = value.split(',').map((x) => parseInt(x, 10));
            }
            // Query params inherently can't be usefully typed; just assume they came in correctly
            // @ts-ignore
            valueFromQuery[key] = value;
        });
        return valueFromQuery;
    });
    const [dimensions, setDimensions] = useState({
        height: window.innerHeight,
        width: window.innerWidth
    })
    const [showMeetRankingsVal, setShowMeetRankings] = useState(showMeetRankings);
    const { isConnected, send, now: nowCallback } = usePersistentWebsocketWithTime(liveUpdateUrl, (data : DisplayCommand<unknown> | {
        type: 'SHOW_MEET_RANKINGS',
        value: boolean
    }) => {
        if (data.type === 'SHOW_MEET_RANKINGS') {
            setShowMeetRankings(data.value);
            return;
        }
        if (data.type === DisplayCommandType.SET_CONFIG) {
            console.log("Remote config command:", data);
            updateConfig((data as DisplayCommand<SetConfig>).params.config);
            return;
        }
        if(isPitUpdate(data)) {
            updateRankingData(data.params.message as PitMessage<unknown>);
            return;
        }
        if(isRedirect(data)) {
            const displayParams = data.params.displayParams
            // Parse because temporal doesn't auto-deserialize via JSON
            displayParams.matchTiming = parseMatchTiming(displayParams.matchTiming);
            triggerRedirect(displayParams as DisplayParamsType, data.params.field)
        }
        dispatchUpdate(data);
    }, (s) => s.send(JSON.stringify(config)));

    function updateConfig(newConfig : DisplayConfig) {
        setConfig(newConfig);
        window.history.replaceState(displayParams, '', window.location.pathname + '?' + PermissiveURLSearchParams({...newConfig}).toString())
        send(JSON.stringify(newConfig));
    }

    useEffect(() => {
        function handleResize() {
            setDimensions({
                height: window.innerHeight,
                width: window.innerWidth
            })
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const cursorMovedRecently = useCursorMovedRecently();

    const triggerRedirect = useCallback((newParams: DisplayParamsType, field: number) => {
        const bindingField = (field || 'all').toString();
        console.log(`Redirecting to display for event: ${newParams.eventCode} (field ${field})`)
        const targetConfig = Object.assign<{}, DisplayConfig, Partial<DisplayConfig>>({}, config, {bindToField: bindingField})
        setConfig(targetConfig)
        window.history.pushState({}, '', window.location.pathname.replace(new RegExp(`/${eventCode}/`), `/${newParams.eventCode}/`) + '?' + PermissiveURLSearchParams({...targetConfig}).toString())
        send(JSON.stringify(targetConfig));
        reconfigure(newParams);
    }, [eventCode, config, displayParams])
    // For development
    // @ts-ignore
    window['triggerRedirect'] = triggerRedirect

    const qualsStarted = (rankingData[divisionNumber || 0] && rankingData[divisionNumber || 0].matchesPlayed > 0) || (!!isDualDivisionParent && Object.entries(rankingData).filter(([num, _]) => num != '0').some(([_, data]) => data.matchesPlayed > 0));
    const inElims = rankingData[divisionNumber || 0]?.inElims
    const inDivisionalElims = isDualDivisionParent && Object.entries(rankingData).filter(([num, _]) => num != '0').every(([_, data]) => data.inElims) || false
    const lastUpdate = getLastRelevantUpdate(updates, config, displayParams.matchTiming?.total, fieldCount ?? 1, qualsStarted, inElims, inDivisionalElims);
    // Disable showing timeouts in FCMP parent, as the show guarantees they are irrelevant
    const lastTimeout = getLastRelevantTimeout(isFirstChampionship && isDualDivisionParent ? {} : updates, config, fieldCount ?? 1);
    const lastRelevantScore = getLastRelevantScoreUpdate(updates, lastUpdate);

    // filter assumedNext to the matches relevant to this field (or any if field==0) and pull the first one
    const nextMatch = lastTimeout?.params.assumedNext.find(m => {
        if (config.bindToField === 'all') return true;
        const field = parseInt(config.bindToField);
        return m.field === field || m.field === 0;
    });
    const nextMatchIndex = lastTimeout?.params.assumedNext.indexOf(nextMatch);
    const expected = nextMatch && lastTimeout?.params.timeouts ? getExpectedStartTimer(nextMatch, lastTimeout?.params.timeouts, isApplicableTimeout, nowCallback) : lastTimeout?.params.timeouts.find(isT206DependencyTimeout);

    const timeoutTimer = useTimer({
        startAt: expected?.timeoutStart,
        duration:  (expected?.timeoutDuration ?? 0) / 1000,
        nowCallback,
        // TODO unmute on receipt of command - for the next timer only!
        sounds: true ? {} : {
            [60]: 'endgameWarning',
            [0]: 'matchEnd'
        }
    });

    const pageParams: PageParams<unknown> = {
        scoreUpdate: lastRelevantScore,
        command: lastUpdate,
        config,
        nowCallback,
        latestTimeout: lastTimeout,
        showLeagueRankings: isLeagueMeet && showMeetRankingsVal && !missingLeague,
    }

    let page;

    if(narrowPageParams(isShowBlank, pageParams)) {
        page = <Blank {...pageParams} />
    } else if(narrowPageParams(isShowVideo, pageParams)) {
        page = <Video {...pageParams} />
    } else if(narrowPageParams(isShowKey, pageParams)) {
        page = <AudienceKey {...pageParams} />
    } else if(narrowPageParams(isShowSetup, pageParams) || narrowPageParams(isShowPreview, pageParams)) {
        page = <Preview {...pageParams} />
    } else if(narrowPageParams(isShowRankings, pageParams)) {
        page = <Rankings {...pageParams} />
    } else if(narrowPageParams(isShowInspectionStatus, pageParams)) {
        page = <InspectionStatus {...pageParams} />
    } else if(narrowPageParams(isShowSponsors, pageParams)) {
        page = <Sponsors />
    } else if(narrowPageParams(isRandomization, pageParams)) {
        page = <Randomization {...pageParams} />
    } else if(narrowPageParams(isInMatchUpdate, pageParams)) {
        page = <Match {...pageParams} />
    } else if(narrowPageParams(isShowResults, pageParams)) {
        page = <Results {...pageParams} />
    } else if(narrowPageParams(isShowRoundResults, pageParams)) {
        page = <RoundResults {...pageParams} />
    } else if(narrowPageParams(isShowMessage, pageParams)) {
        page = <Message {...pageParams} />
    } else if(narrowPageParams(isShowWifi, pageParams)) {
        page = <WifiReminder />
    } else if(narrowPageParams(isShowSafetySecurity, pageParams)) {
        page = <SafetySecurity />
    } else if(narrowPageParams(isShowSlideshow, pageParams)) {
        page = <Slideshow {...pageParams} />
    } else if(narrowPageParams(isShowSelection, pageParams)) {
        page = <AllianceSelection {...pageParams} />
    } else if(narrowPageParams(isShowBracket, pageParams)) {
        page = <Bracket config={config}/>
    } else if(narrowPageParams(isShowRotatingBracket, pageParams)) {
        page = <RotatingBracket config={config}/>
    } else if(narrowPageParams(isShowOnlineResults, pageParams)) {
        page = <OnlineResults {...pageParams} />
    } else if(narrowPageParams(isTimeoutUpdate, pageParams)) {
        // TODO restore the Timeout page for explicit show command from Control
        page = <Blank {...pageParams} />
    } else if(narrowPageParams(isShowAward, pageParams)) {
        page = <Award {...pageParams} />
    } else if(narrowPageParams(isShowAdvancement, pageParams)) {
        page = <Advancement {...pageParams} />
    } else if(narrowPageParams(isPlayVideo, pageParams)) {
        page = <PlayVideo {...pageParams} />
    } else if(narrowPageParams(isRedirect, pageParams)) {
        page = <Blank {...pageParams} />
    } else {
        page = <Debug {...pageParams} />
    }

    const xZoom = dimensions.width / (config.type == 'cmpBar' ? 896 : 1920);
    const yZoom = dimensions.height / (config.type == 'cmpBar' ? 128 : 1080);
    const displayScale = Math.min(xZoom, yZoom);
    // as DisplayCommand<any> is gross, but I can't think of a typesafe way to claim a page is *actually* getting the
    // correct parameters
    return <div className={`displayRoot ${config.overlay && config.overlayColor === 'transparent' ? 'transparent' : ''}${config.type == 'cmpBar' ? 'cmpBar' : ''} ${cursorMovedRecently ? 'show-cursor' : ''}`} style={{ "--display-scale": displayScale }}>
        {displayParams.avatarUrl ? <link rel="stylesheet" href={displayParams.avatarUrl} /> : null}
        <Config config={config} setConfig={updateConfig} fieldCount={fieldCount || 1} isConfigured={!window.location.search}
                isDualDivisionParent={isDualDivisionParent} style={style} showMeetRankings={isLeagueMeet && showMeetRankingsVal} eventName={eventName}
                cursorMovedRecently={cursorMovedRecently} divisions={displayParams.divisions} />
        <div className="displayContainer">
            <DisplayErrorBoundary>
                <Timeout.Provider value={{timer: timeoutTimer, nextMatch: nextMatch, nextMatchIndex: nextMatchIndex, timeout: expected}}>
                <RankingData.Provider value={rankingData}>
                    <TransitionGroup component={null}>
                        {/* Play video must not  transition or the audio will continue to play while it is invisible */}
                        <CSSTransition key={transitionKey(lastUpdate.type)} timeout={lastUpdate.init || lastUpdate.type == 'PLAY_VIDEO' ? 0 : 5000} classNames="page">
                            {page}
                        </CSSTransition>
                    </TransitionGroup>
                </RankingData.Provider>
                </Timeout.Provider>
            </DisplayErrorBoundary>
            <EnsureAudio config={config} />
            {isConnected ? null : <DisconnectedBanner />}
        </div>
    </div>
}